<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0" style="color: #1f2937;">
        <v-icon class="mr-2" style="color: #6b7280;">mdi-target</v-icon>
        New Opportunities from Tracked Buyers
      </h3>
    </v-card-title>
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0" style="color: #6b7280;">Recent tenders from your prospects and customers</p>
    </div>

    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </div>

    <div v-else-if="!activities || activities.length === 0" class="text-center py-8 grey--text">
      No buyer activity found
    </div>

    <v-card-text v-else class="pa-0">
      <div 
        v-for="(activity, index) in paginatedActivities" 
        :key="`activity-${activity.tender_id}-${index}`"
        class="activity-card pa-6"
        :class="{ 'activity-card-border-top': index > 0 }"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <div class="d-flex align-center gap-2 mb-2">
              <v-chip x-small color="green" text-color="white">
                {{ activity.cluster_name }} ({{ activity.cluster_type }})
              </v-chip>
              <v-chip v-if="activity.value > 1000000" x-small color="blue" text-color="blue-darken-4">
                <v-icon x-small left>mdi-star</v-icon>
                MAJOR OPPORTUNITY
              </v-chip>
            </div>

            <div class="text-body-2 mb-2">
              <span class="font-weight-semibold">
                <router-link 
                  v-if="activity.publisher_id" 
                  :to="`/entity/${activity.publisher_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ activity.publisher_name }}
                </router-link>
                <span v-else>{{ activity.publisher_name }}</span>
              </span>
              <span class="grey--text"> published a new tender</span>
            </div>

            <router-link 
              :to="`/tender/${activity.tender_id}`" 
              class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-3"
              style="color: #1f2937;"
              target="_blank"
            >
              {{ activity.title }}
              <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
            </router-link>

            <div class="d-flex align-center gap-4 text-body-2 grey--text mb-3">
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-cash</v-icon>
                {{ formatCurrency(activity.value) }}
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                Published {{ formatDate(activity.contract_start) }}
              </span>
              <span 
                v-if="activity.closing_date" 
                class="d-flex align-center font-weight-semibold"
                :style="`color: ${getClosingDateColor(activity.closing_date)};`"
              >
                <v-icon small class="mr-1">mdi-alarm</v-icon>
                Closes: {{ formatDate(activity.closing_date) }} ({{ getDaysUntilClose(activity.closing_date) }} days)
              </span>
            </div>

            <div 
              v-if="activity.value > 1000000" 
              class="pa-3" 
              style="background-color: #d1fae5; border-radius: 4px;"
            >
              <div class="text-body-2" style="color: #4b5563; font-style: italic; font-size: 13px;">
                This is a major opportunity worth {{ formatCurrency(activity.value) }}.
              </div>
            </div>
          </div>

          <div class="d-flex flex-column ml-4" style="gap: 8px;">
            <v-btn
              small
              depressed
              dark
              class="body-2 text-capitalize"
              color="#10b981"
              text-color="white"
              style="font-size: 13px;"
              :to="`/tender/${activity.tender_id}`"
              target="_blank"
            >
              <v-icon small left>mdi-eye</v-icon>
              View Tender
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="text-capitalize body-2"
              style="font-size: 13px;"
              @click.stop="handleAddToPipeline(activity)"
            >
              <v-icon small left>mdi-bookmark-plus</v-icon>
              Add to Pipeline
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <v-card-actions 
        v-if="showPagination" 
        class="pa-4" 
        style="border-top: 1px solid #e5e7eb; background-color: #f9fafb;"
      >
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div class="text-body" style="color: #6b7280;">
            Showing {{ paginationStart }} - {{ paginationEnd }} of {{ activities.length }}
          </div>
          <div class="d-flex gap-2">
            <v-btn
              icon
              x-small
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="currentPage >= totalPages"
              @click="goToNextPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'BuyerActivitySection',
  
  props: {
    activities: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      currentPage: 1
    };
  },

  computed: {
    paginatedActivities() {
      if (!this.activities || this.activities.length === 0) return [];
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.activities.slice(start, end);
    },

    totalPages() {
      if (!this.activities || this.activities.length === 0) return 0;
      return Math.ceil(this.activities.length / this.itemsPerPage);
    },

    showPagination() {
      return this.activities && this.activities.length > this.itemsPerPage;
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.activities.length);
    }
  },

  watch: {
    activities() {
      // Reset to first page when activities change
      this.currentPage = 1;
    }
  },

  methods: {
    handleAddToPipeline(activity) {
      this.$emit('add-to-pipeline', activity);
    },

    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    formatCurrency(value) {
      if (!value) return "$0";
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    },

    getClosingDateColor(closingDate) {
      const days = this.getDaysUntilClose(closingDate);
      if (days <= 7) return '#dc2626';
      if (days <= 14) return '#d97706';
      return '#059669';
    },

    getDaysUntilClose(closingDate) {
      if (!closingDate) return 0;
      try {
        const closeDate = new Date(closingDate);
        const today = new Date();
        const diffTime = closeDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      } catch (e) {
        return 0;
      }
    }
  }
};
</script>

<style scoped>
.activity-card {
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f0fdf4;
  border-left: 3px solid #4ade80;
  transition: background-color 0.2s ease;
}

.activity-card:hover {
  background-color: #e8f5e9;
}

.activity-card-border-top {
  border-top: 1px solid #e5e7eb;
}
</style>